import React from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { formatIDR, ASSET_STATUS } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { Plus, Boxes, Package as PackageIcon, Trash2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const emptyProduct = { name: "", brand: "", category: "", kind: "tracked", ownership: "inhouse", vendor_name: "", daily_price: 0, replacement_value: 0, total_qty: 0, min_stock: 0, damaged_qty: 0, sku: "", description: "", location: "", active: true, initial_assets: [] };
const emptyUnit = { asset_code: "", serial_number: "", condition: "Baik", location: "" };

export default function InventoryPage() {
  const [products, setProducts] = React.useState([]);
  const [cats, setCats] = React.useState([]);
  const [brands, setBrands] = React.useState([]);
  const [openP, setOpenP] = React.useState(false);
  const [form, setForm] = React.useState(emptyProduct);
  const [editId, setEditId] = React.useState(null);
  const [openAsset, setOpenAsset] = React.useState(false);
  const [assetForm, setAssetForm] = React.useState({ product_id: "", asset_code: "", serial_number: "", condition: "Baik", location: "", status: "tersedia", purchase_price: 0 });
  const [openCat, setOpenCat] = React.useState(false);
  const [catName, setCatName] = React.useState("");
  const [openBrand, setOpenBrand] = React.useState(false);
  const [brandName, setBrandName] = React.useState("");
  const [ownershipFilter, setOwnershipFilter] = React.useState("");

  const load = React.useCallback(async () => {
    const [p, c, b] = await Promise.all([api.get("/products"), api.get("/categories"), api.get("/brands")]);
    setProducts(p.data); setCats(c.data); setBrands(b.data);
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      if (editId) {
        await api.patch(`/products/${editId}`, form);
        toast.success("Tersimpan");
      } else {
        const { data } = await api.post("/products", form);
        toast.success(`Tersimpan${data.created_assets ? ` · ${data.created_assets} unit fisik dibuat otomatis` : ""}`);
      }
      setOpenP(false); setForm(emptyProduct); setEditId(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const edit = (p) => { setForm(p); setEditId(p.id); setOpenP(true); };

  const deleteProduct = async (pid) => {
    try { await api.delete(`/products/${pid}`); toast.success("Produk dinonaktifkan"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const hardDeleteProduct = async (pid) => {
    try {
      const { data } = await api.delete(`/products/${pid}/permanent`);
      toast.success(`Produk dihapus permanen${data.assets_deleted ? ` beserta ${data.assets_deleted} unit fisik` : ""}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveAsset = async () => {
    if (!assetForm.product_id) return toast.error("Pilih produk terlebih dahulu");
    try {
      // If code empty, use bulk endpoint which auto-generates
      if (!assetForm.asset_code.trim()) {
        const { data } = await api.post(`/products/${assetForm.product_id}/assets/bulk`, {
          assets: [{
            serial_number: assetForm.serial_number,
            condition: assetForm.condition,
            location: assetForm.location,
            status: assetForm.status,
          }],
        });
        toast.success(`Aset ${data.assets[0].asset_code} dibuat`);
      } else {
        await api.post("/assets", assetForm);
        toast.success("Aset ditambahkan");
      }
      setOpenAsset(false);
      setAssetForm({ product_id: "", asset_code: "", serial_number: "", condition: "Baik", location: "", status: "tersedia", purchase_price: 0 });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveCat = async () => {
    try { await api.post("/categories", { name: catName, active: true }); toast.success("Kategori dibuat"); setCatName(""); setOpenCat(false); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const saveBrand = async () => {
    try { await api.post("/brands", { name: brandName, active: true }); toast.success("Brand dibuat"); setBrandName(""); setOpenBrand(false); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="inventory-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>Inventory</h1>
          <p className="text-sm text-neutral-500">Kelola equipment, aset, kategori, dan brand.</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={openCat} onOpenChange={setOpenCat}>
            <DialogTrigger asChild><Button variant="outline" size="sm" data-testid="btn-new-category">+ Kategori</Button></DialogTrigger>
            <DialogContent><DialogHeader><DialogTitle>Kategori Baru</DialogTitle></DialogHeader>
              <Input value={catName} onChange={(e) => setCatName(e.target.value)} placeholder="Nama kategori" />
              <DialogFooter><Button onClick={saveCat} className="bg-green-600 hover:bg-green-700">Simpan</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={openBrand} onOpenChange={setOpenBrand}>
            <DialogTrigger asChild><Button variant="outline" size="sm" data-testid="btn-new-brand">+ Brand</Button></DialogTrigger>
            <DialogContent><DialogHeader><DialogTitle>Brand Baru</DialogTitle></DialogHeader>
              <Input value={brandName} onChange={(e) => setBrandName(e.target.value)} placeholder="Nama brand" />
              <DialogFooter><Button onClick={saveBrand} className="bg-green-600 hover:bg-green-700">Simpan</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={openP} onOpenChange={(v) => { setOpenP(v); if (!v) { setForm(emptyProduct); setEditId(null); } }}>
            <DialogTrigger asChild><Button className="bg-green-600 hover:bg-green-700" data-testid="btn-new-product"><Plus className="h-4 w-4 mr-1" />Equipment Baru</Button></DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>{editId ? "Edit Equipment" : "Equipment Baru"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Nama</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="p-name" /></div>
                <div><Label>Brand</Label>
                  <select className="w-full border border-neutral-200 rounded-md px-3 py-2 text-sm" value={form.brand || ""} onChange={(e) => setForm({ ...form, brand: e.target.value })}>
                    <option value="">-</option>{brands.map((b) => <option key={b.id} value={b.name}>{b.name}</option>)}
                  </select>
                </div>
                <div><Label>Kategori</Label>
                  <select className="w-full border border-neutral-200 rounded-md px-3 py-2 text-sm" value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                    <option value="">-</option>{cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                  </select>
                </div>
                <div><Label>Tipe Inventory</Label>
                  <select className="w-full border border-neutral-200 rounded-md px-3 py-2 text-sm" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="p-kind">
                    <option value="tracked">Individually Tracked</option>
                    <option value="bulk">Quantity-Based</option>
                  </select>
                </div>
                <div><Label>Kepemilikan</Label>
                  <select className="w-full border border-neutral-200 rounded-md px-3 py-2 text-sm" value={form.ownership || "inhouse"} onChange={(e) => setForm({ ...form, ownership: e.target.value })} data-testid="p-ownership">
                    <option value="inhouse">Inhouse (Milik Grippel)</option>
                    <option value="vendor">Barang Vendor</option>
                  </select>
                </div>
                {form.ownership === "vendor" && (
                  <div className="col-span-2"><Label>Nama Vendor</Label><Input value={form.vendor_name || ""} onChange={(e) => setForm({ ...form, vendor_name: e.target.value })} placeholder="Contoh: Vendor XYZ" data-testid="p-vendor" /></div>
                )}
                <div><Label>Harga/hari (IDR)</Label><Input type="number" value={form.daily_price} onChange={(e) => setForm({ ...form, daily_price: +e.target.value })} data-testid="p-price" /></div>
                <div><Label>Replacement Value (IDR)</Label><Input type="number" value={form.replacement_value} onChange={(e) => setForm({ ...form, replacement_value: +e.target.value })} /></div>
                {form.kind === "tracked" && !editId && (
                  <div className="col-span-2 p-3 rounded border border-green-200 bg-green-50 space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-sm font-semibold">Daftar Unit Fisik (Aset)</Label>
                        <div className="text-[10px] text-neutral-600">Tambahkan setiap unit fisik dengan serial number-nya. Kode aset dikosongkan = auto-generate.</div>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => setForm({ ...form, initial_assets: [...(form.initial_assets || []), { ...emptyUnit }] })} data-testid="p-add-unit">
                        <Plus className="h-3 w-3 mr-1" /> Tambah Unit
                      </Button>
                    </div>
                    {(form.initial_assets || []).length === 0 && (
                      <div className="text-xs text-neutral-500 py-2">Belum ada unit fisik. Klik "Tambah Unit" untuk mulai.</div>
                    )}
                    {(form.initial_assets || []).map((u, ui) => (
                      <div key={ui} className="grid grid-cols-12 gap-2 items-center bg-white p-2 rounded border border-neutral-200" data-testid={`p-unit-row-${ui}`}>
                        <div className="col-span-1 text-xs text-neutral-500 text-center">{ui + 1}</div>
                        <Input placeholder="Kode (auto jika kosong)" className="col-span-3 text-xs" value={u.asset_code} onChange={(e) => {
                          const arr = [...form.initial_assets]; arr[ui] = { ...arr[ui], asset_code: e.target.value }; setForm({ ...form, initial_assets: arr });
                        }} data-testid={`p-unit-code-${ui}`} />
                        <Input placeholder="Serial Number" className="col-span-4 text-xs" value={u.serial_number} onChange={(e) => {
                          const arr = [...form.initial_assets]; arr[ui] = { ...arr[ui], serial_number: e.target.value }; setForm({ ...form, initial_assets: arr });
                        }} data-testid={`p-unit-sn-${ui}`} />
                        <Input placeholder="Kondisi" className="col-span-2 text-xs" value={u.condition} onChange={(e) => {
                          const arr = [...form.initial_assets]; arr[ui] = { ...arr[ui], condition: e.target.value }; setForm({ ...form, initial_assets: arr });
                        }} />
                        <Input placeholder="Lokasi" className="col-span-1 text-xs" value={u.location} onChange={(e) => {
                          const arr = [...form.initial_assets]; arr[ui] = { ...arr[ui], location: e.target.value }; setForm({ ...form, initial_assets: arr });
                        }} />
                        <button onClick={() => setForm({ ...form, initial_assets: form.initial_assets.filter((_, x) => x !== ui) })} className="col-span-1 text-red-500 justify-self-center" data-testid={`p-unit-remove-${ui}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {form.kind === "bulk" && <>
                  <div><Label>SKU</Label><Input value={form.sku || ""} onChange={(e) => setForm({ ...form, sku: e.target.value })} /></div>
                  <div><Label>Total Qty</Label><Input type="number" value={form.total_qty} onChange={(e) => setForm({ ...form, total_qty: +e.target.value })} /></div>
                  <div><Label>Min Stock</Label><Input type="number" value={form.min_stock} onChange={(e) => setForm({ ...form, min_stock: +e.target.value })} /></div>
                  <div><Label>Rusak (Qty)</Label><Input type="number" value={form.damaged_qty} onChange={(e) => setForm({ ...form, damaged_qty: +e.target.value })} /></div>
                </>}
                <div className="col-span-2"><Label>Deskripsi</Label><Textarea rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              </div>
              <DialogFooter><Button onClick={save} className="bg-green-600 hover:bg-green-700" data-testid="save-product">Simpan</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Tabs defaultValue="products">
        <TabsList>
          <TabsTrigger value="products" data-testid="tab-products"><PackageIcon className="h-3.5 w-3.5 mr-1" />Produk</TabsTrigger>
          <TabsTrigger value="assets" data-testid="tab-assets"><Boxes className="h-3.5 w-3.5 mr-1" />Aset Fisik</TabsTrigger>
        </TabsList>

        <TabsContent value="products">
          <div className="mb-3 flex gap-2 items-center">
            <Label className="text-xs">Filter:</Label>
            <select
              value={ownershipFilter}
              onChange={(e) => setOwnershipFilter(e.target.value)}
              className="border border-neutral-200 rounded-md px-2 py-1 text-sm bg-white"
              data-testid="ownership-filter"
            >
              <option value="">Semua Kepemilikan</option>
              <option value="inhouse">Inhouse</option>
              <option value="vendor">Vendor</option>
            </select>
          </div>
          <div className="card-flat overflow-x-auto">
            <table className="w-full data-table">
              <thead><tr>
                <th className="text-left">Nama</th>
                <th className="text-left">Brand</th>
                <th className="text-left">Kategori</th>
                <th className="text-left">Tipe</th>
                <th className="text-left">Kepemilikan</th>
                <th className="text-right">Harga/hari</th>
                <th className="text-right">Qty / Aset</th>
                <th></th>
              </tr></thead>
              <tbody>
                {products
                  .filter((p) => !ownershipFilter || (p.ownership || "inhouse") === ownershipFilter)
                  .map((p) => (
                  <tr key={p.id} data-testid={`product-row-${p.id}`}>
                    <td className="font-medium">{p.name}</td>
                    <td className="text-xs">{p.brand || "-"}</td>
                    <td className="text-xs">{p.category || "-"}</td>
                    <td className="text-xs">{p.kind === "tracked" ? "Tracked" : "Bulk"}</td>
                    <td className="text-xs">
                      {(p.ownership || "inhouse") === "vendor" ? (
                        <span
                          className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 text-amber-700 px-2 py-0.5 text-xs"
                          data-testid={`badge-vendor-${p.id}`}
                        >
                          Vendor{p.vendor_name ? ` · ${p.vendor_name}` : ""}
                        </span>
                      ) : (
                        <span
                          className="inline-flex items-center rounded-full border border-green-200 bg-green-50 text-green-700 px-2 py-0.5 text-xs"
                          data-testid={`badge-inhouse-${p.id}`}
                        >
                          Inhouse
                        </span>
                      )}
                    </td>
                    <td className="text-right">{formatIDR(p.daily_price)}</td>
                    <td className="text-right text-xs">{p.kind === "tracked" ? `${p._asset_count || 0} unit` : `${p.total_qty} qty`}</td>
                    <td className="text-right whitespace-nowrap">
                      {p.kind === "tracked" && (
                        <button
                          className="text-green-700 text-xs hover:underline mr-3"
                          onClick={async () => {
                            const n = parseInt(prompt(`Berapa unit fisik ${p.name} yang ingin ditambahkan?`, "1"));
                            if (!n || n <= 0) return;
                            try {
                              const { data } = await api.post(`/products/${p.id}/assets/generate`, { count: n });
                              toast.success(`${data.created} unit dibuat: ${data.assets.map(a => a.asset_code).join(", ")}`);
                              load();
                            } catch (e) { toast.error(formatApiError(e)); }
                          }}
                          data-testid={`gen-assets-${p.id}`}
                        >+ Unit</button>
                      )}
                      <button className="text-green-700 text-xs hover:underline mr-3" onClick={() => edit(p)} data-testid={`edit-product-${p.id}`}>Edit</button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button className="text-amber-600 text-xs hover:underline mr-3" data-testid={`delete-product-${p.id}`}>Nonaktifkan</button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Nonaktifkan produk {p.name}?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Produk akan dinonaktifkan (soft delete). Data booking historis tetap tersimpan dan tidak terpengaruh. Anda dapat mengaktifkan kembali via edit.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Batal</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deleteProduct(p.id)} className="bg-amber-600 hover:bg-amber-700" data-testid={`confirm-delete-product-${p.id}`}>Ya, Nonaktifkan</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button className="text-red-600 text-xs hover:underline font-medium" data-testid={`hard-delete-product-${p.id}`}>Hapus Permanen</button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle className="text-red-600">⚠️ Hapus permanen produk {p.name}?</AlertDialogTitle>
                            <AlertDialogDescription>
                              <b>PERINGATAN:</b> Aksi ini akan <b>menghapus produk beserta semua unit fisik/aset yang terkait</b> dari database secara permanen. Tindakan ini <b>tidak bisa dibatalkan</b>.
                              <br /><br />
                              Sistem akan menolak jika produk masih tercatat di booking atau paket. Untuk produk yang punya riwayat transaksi, gunakan opsi <b>Nonaktifkan</b>.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Batal</AlertDialogCancel>
                            <AlertDialogAction onClick={() => hardDeleteProduct(p.id)} className="bg-red-600 hover:bg-red-700" data-testid={`confirm-hard-delete-product-${p.id}`}>Ya, Hapus Permanen</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="assets">
          <div className="mb-3">
            <Dialog open={openAsset} onOpenChange={setOpenAsset}>
              <DialogTrigger asChild><Button size="sm" className="bg-green-600 hover:bg-green-700" data-testid="btn-new-asset"><Plus className="h-4 w-4 mr-1" />Aset Fisik</Button></DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>Aset Fisik Baru</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <Label>Produk <span className="text-red-500">*</span></Label>
                    <select
                      className="w-full border border-neutral-200 rounded-md px-3 py-2 text-sm bg-white"
                      value={assetForm.product_id}
                      onChange={(e) => setAssetForm({ ...assetForm, product_id: e.target.value })}
                      data-testid="asset-product-select"
                    >
                      <option value="">— pilih produk (tracked) —</option>
                      {products
                        .filter((p) => p.kind === "tracked" && p.active !== false)
                        .map((p) => (
                          <option key={p.id} value={p.id}>{p.name}{p.brand ? ` · ${p.brand}` : ""}</option>
                        ))}
                    </select>
                    <div className="text-[10px] text-neutral-500 mt-0.5">
                      {products.filter((p) => p.kind === "tracked").length} produk tracked tersedia
                    </div>
                  </div>
                  <div><Label>Kode Aset <span className="text-neutral-400 text-[10px]">(auto jika kosong)</span></Label><Input placeholder="auto-generate" value={assetForm.asset_code} onChange={(e) => setAssetForm({ ...assetForm, asset_code: e.target.value })} data-testid="asset-code-input" /></div>
                  <div><Label>Serial Number</Label><Input value={assetForm.serial_number} onChange={(e) => setAssetForm({ ...assetForm, serial_number: e.target.value })} data-testid="asset-sn-input" /></div>
                  <div><Label>Kondisi</Label><Input value={assetForm.condition} onChange={(e) => setAssetForm({ ...assetForm, condition: e.target.value })} /></div>
                  <div><Label>Lokasi</Label><Input value={assetForm.location} onChange={(e) => setAssetForm({ ...assetForm, location: e.target.value })} /></div>
                </div>
                <DialogFooter><Button onClick={saveAsset} className="bg-green-600 hover:bg-green-700" data-testid="save-asset">Simpan</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <AssetList products={products} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function AssetList({ products }) {
  const [assets, setAssets] = React.useState([]);
  const [qrAsset, setQrAsset] = React.useState(null);
  const [editAsset, setEditAsset] = React.useState(null);
  const [form, setForm] = React.useState(null);
  const load = React.useCallback(async () => { const { data } = await api.get("/assets"); setAssets(data); }, []);
  React.useEffect(() => { load(); }, [load]);
  const productName = (id) => products.find((p) => p.id === id)?.name || "-";
  const qrUrl = qrAsset ? `${process.env.REACT_APP_BACKEND_URL}/api/assets/${qrAsset.id}/qrcode` : null;

  const openEdit = (a) => {
    setEditAsset(a);
    setForm({
      product_id: a.product_id, asset_code: a.asset_code, serial_number: a.serial_number || "",
      condition: a.condition || "Baik", location: a.location || "", status: a.status || "tersedia",
      purchase_price: a.purchase_price || 0, notes: a.notes || "",
    });
  };
  const saveEdit = async () => {
    try { await api.patch(`/assets/${editAsset.id}`, form); toast.success("Aset diperbarui"); setEditAsset(null); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteAsset = async (aid) => {
    try { await api.delete(`/assets/${aid}`); toast.success("Aset dinonaktifkan"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const hardDeleteAsset = async (aid) => {
    try { await api.delete(`/assets/${aid}/permanent`); toast.success("Aset dihapus permanen"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <>
      <div className="card-flat overflow-x-auto">
        <table className="w-full data-table">
          <thead><tr>
            <th className="text-left">Kode</th>
            <th className="text-left">Produk</th>
            <th className="text-left">SN</th>
            <th className="text-left">Kondisi</th>
            <th className="text-left">Lokasi</th>
            <th className="text-left">Status</th>
            <th></th>
          </tr></thead>
          <tbody>
            {assets.map((a) => (
              <tr key={a.id} data-testid={`asset-row-${a.id}`}>
                <td className="font-mono text-xs">{a.asset_code}</td>
                <td>{productName(a.product_id)}</td>
                <td className="text-xs">{a.serial_number || "-"}</td>
                <td className="text-xs">{a.condition}</td>
                <td className="text-xs">{a.location || "-"}</td>
                <td><StatusBadge statusMap={ASSET_STATUS} value={a.status} /></td>
                <td className="text-right whitespace-nowrap">
                  <button className="text-green-700 text-xs hover:underline mr-3" onClick={() => openEdit(a)} data-testid={`edit-asset-${a.id}`}>Edit</button>
                  <button className="text-green-700 text-xs hover:underline mr-3" onClick={() => setQrAsset(a)} data-testid={`qr-asset-${a.id}`}>QR</button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button className="text-amber-600 text-xs hover:underline mr-3" data-testid={`delete-asset-${a.id}`}>Nonaktifkan</button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Nonaktifkan aset {a.asset_code}?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Aset akan ditandai <b>Tidak Aktif</b> dan tidak muncul lagi di kalkulasi ketersediaan. Data booking historis tetap terjaga. Aset yang sedang disewa/dipesan tidak bisa dihapus.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Batal</AlertDialogCancel>
                        <AlertDialogAction onClick={() => deleteAsset(a.id)} className="bg-amber-600 hover:bg-amber-700" data-testid={`confirm-delete-asset-${a.id}`}>Ya, Nonaktifkan</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button className="text-red-600 text-xs hover:underline font-medium" data-testid={`hard-delete-asset-${a.id}`}>Hapus Permanen</button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle className="text-red-600">⚠️ Hapus permanen aset {a.asset_code}?</AlertDialogTitle>
                        <AlertDialogDescription>
                          <b>PERINGATAN:</b> Aset ini akan dihapus dari database secara permanen. Tindakan ini <b>tidak bisa dibatalkan</b>.
                          <br /><br />
                          Sistem akan menolak jika aset masih tercatat di booking. Untuk aset dengan riwayat transaksi, gunakan <b>Nonaktifkan</b>.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Batal</AlertDialogCancel>
                        <AlertDialogAction onClick={() => hardDeleteAsset(a.id)} className="bg-red-600 hover:bg-red-700" data-testid={`confirm-hard-delete-asset-${a.id}`}>Ya, Hapus Permanen</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editAsset} onOpenChange={(v) => !v && setEditAsset(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Aset {editAsset?.asset_code}</DialogTitle></DialogHeader>
          {form && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Produk</Label>
                <select className="w-full border border-neutral-200 rounded-md px-3 py-2 text-sm" value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })}>
                  {products.filter((p) => p.kind === "tracked").map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div><Label>Kode Aset</Label><Input value={form.asset_code} onChange={(e) => setForm({ ...form, asset_code: e.target.value })} data-testid="edit-asset-code" /></div>
              <div><Label>Serial Number</Label><Input value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} data-testid="edit-asset-sn" /></div>
              <div><Label>Kondisi</Label><Input value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })} /></div>
              <div><Label>Lokasi</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="edit-asset-location" /></div>
              <div><Label>Status</Label>
                <select className="w-full border border-neutral-200 rounded-md px-3 py-2 text-sm" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="edit-asset-status">
                  {Object.entries(ASSET_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              <div><Label>Harga Beli</Label><Input type="number" value={form.purchase_price} onChange={(e) => setForm({ ...form, purchase_price: +e.target.value })} /></div>
            </div>
          )}
          <DialogFooter><Button onClick={saveEdit} className="bg-green-600 hover:bg-green-700" data-testid="save-asset-edit">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!qrAsset} onOpenChange={(v) => !v && setQrAsset(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>QR Code Aset</DialogTitle></DialogHeader>
          {qrAsset && (
            <div className="flex flex-col items-center gap-3 py-3">
              <img src={qrUrl} alt={qrAsset.asset_code} className="w-56 h-56 border border-neutral-200 rounded" data-testid="qr-image" />
              <div className="text-center">
                <div className="font-mono text-lg font-bold">{qrAsset.asset_code}</div>
                <div className="text-xs text-neutral-500">{productName(qrAsset.product_id)}</div>
                <div className="text-xs text-neutral-500">SN: {qrAsset.serial_number || "-"}</div>
              </div>
              <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="print-qr">Cetak</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
